import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { warmupMakeMoveFunction } from './firebase';
import Header from './components/Header';
import Tabs from './components/Tabs';
import ChessGame from './components/ChessGame';
import BenchmarkDisplay from './components/BenchmarkDisplay';
import ScoresDisplay from './components/ScoresDisplay';
import BenchmarkImportPage from './pages/BenchmarkImportPage';
import ArticlePage from './pages/ArticlePage';
import InfoSection from './components/InfoSection';
import Partners from './components/Partners';

import './App.css';

const ADMIN_HASH = "12cdbaef41497552a9244e434044665ade5fe0c13d045ce2faa7103d5e3a77db";

// Custom hook for game limit management (unchanged)
const useGameLimit = (maxGames = 7, hoursPeriod = 24) => {
  const [gamesPlayed, setGamesPlayed] = useState(0);
  const [nextGameTime, setNextGameTime] = useState<Date | null>(null);
  const [limitReached, setLimitReached] = useState(false);

  useEffect(() => {
    // Load game history from localStorage
    const loadGameHistory = () => {
      const gameHistoryStr = localStorage.getItem('gameHistory');
      if (gameHistoryStr) {
        try {
          // Parse timestamps of previous games
          const gameHistory: number[] = JSON.parse(gameHistoryStr);
          const now = Date.now();
          // Filter to only include games from the last 24 hours
          const recentGames = gameHistory.filter(
            timestamp => now - timestamp < hoursPeriod * 60 * 60 * 1000
          );
          
          setGamesPlayed(recentGames.length);
          
          if (recentGames.length >= maxGames) {
            // Sort timestamps to find the earliest one
            const sortedGames = [...recentGames].sort((a, b) => a - b);
            // Calculate when the next game will be available
            const oldestGame = sortedGames[0];
            const nextAvailableTime = new Date(oldestGame + hoursPeriod * 60 * 60 * 1000);
            setNextGameTime(nextAvailableTime);
            setLimitReached(true);
          } else {
            setLimitReached(false);
            setNextGameTime(null);
          }
          
          // Clean up old entries
          if (gameHistory.length !== recentGames.length) {
            localStorage.setItem('gameHistory', JSON.stringify(recentGames));
          }
        } catch (e) {
          console.error("Error parsing game history:", e);
          localStorage.removeItem('gameHistory');
          setGamesPlayed(0);
          setLimitReached(false);
        }
      } else {
        setGamesPlayed(0);
        setLimitReached(false);
      }
    };

    loadGameHistory();
    // Refresh every minute to update the limit status
    const intervalId = setInterval(loadGameHistory, 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, [maxGames, hoursPeriod]);

  // Function to record a new game
  const recordGame = () => {
    if (limitReached) return false;
    const now = Date.now();
    const gameHistoryStr = localStorage.getItem('gameHistory');
    let gameHistory: number[] = [];
    
    if (gameHistoryStr) {
      try {
        gameHistory = JSON.parse(gameHistoryStr);
      } catch (e) {
        console.error("Error parsing game history:", e);
      }
    }
    
    gameHistory.push(now);
    localStorage.setItem('gameHistory', JSON.stringify(gameHistory));
    
    // Update state
    const recentGames = gameHistory.filter(
      timestamp => now - timestamp < hoursPeriod * 60 * 60 * 1000
    );
    
    setGamesPlayed(recentGames.length);
    
    if (recentGames.length >= maxGames) {
      const sortedGames = [...recentGames].sort((a, b) => a - b);
      const oldestGame = sortedGames[0];
      const nextAvailableTime = new Date(oldestGame + hoursPeriod * 60 * 60 * 1000);
      setNextGameTime(nextAvailableTime);
      setLimitReached(true);
    }
    
    return true;
  };

  return { gamesPlayed, limitReached, nextGameTime, recordGame, maxGames };
};

// Componente per gestire l'admin redirect
const AdminHandler: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkAdminAccess = async () => {
      const params = new URLSearchParams(location.search);
      const adminParam = params.get('admin');
      
      if (adminParam) {
        try {
          // Codifica la stringa in un array di byte
          const encoder = new TextEncoder();
          const data = encoder.encode(adminParam);
          
          // Calcola l'hash SHA-256
          const hashBuffer = await crypto.subtle.digest('SHA-256', data);
          
          // Converti l'hash in stringa esadecimale
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
          
          // Confronta con l'hash predefinito
          if (hashHex === ADMIN_HASH) {
            navigate('/admin');
          }
        } catch (error) {
          console.error("Errore nel calcolo dell'hash:", error);
        }
      }
    };
    checkAdminAccess();
  }, [navigate, location.search]);

  return null;
};

// Componente principale dell'app
const AppContent: React.FC = () => {
  const [selectedModel, setSelectedModel] = useState('gpt-4.1');
  const [useVision, setUseVision] = useState(false);
  const gameLimit = useGameLimit(7, 24); // 7 games per 24 hours

  useEffect(() => {
    // warmup main functions
    warmupMakeMoveFunction();
  }, []);

  // Componente per la pagina principale (chess)
  const ChessPage = () => (
    <div className="w-full">
      {gameLimit.limitReached ? (
        <div className="p-6 border border-terminal-green/30 rounded-lg text-center">
          <h2 className="text-xl font-semibold mb-4">Thank you for playing!</h2>
          <p className="mb-3">You've played {gameLimit.gamesPlayed-1} out of {gameLimit.maxGames-1} games allowed in the last 24 hours.</p>
          {gameLimit.nextGameTime && (
            <p className="text-terminal-green">
              Next game available at: {gameLimit.nextGameTime.toLocaleString()}
            </p>
          )}
        </div>
      ) : (
        <>
          <ChessGame 
            selectedModel={selectedModel} 
            useVision={useVision} 
            onModelChange={setSelectedModel} 
            onVisionChange={setUseVision}
            recordGame={gameLimit.recordGame}
          />
          <InfoSection />
          <Partners />
        </>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-bg-dark text-terminal-green flex flex-col">
      <AdminHandler />
      <Header />
      
      <div className="container mx-auto px-2 sm:px-4 flex-grow">
        <Tabs />
        
        <div className="mt-4 md:mt-8">
          <Routes>
            <Route path="/" element={<ChessPage />} />
            <Route path="/benchmarks" element={<BenchmarkDisplay />} />
            <Route path="/scores" element={<ScoresDisplay />} />
            <Route path="/admin" element={<BenchmarkImportPage />} />
            <Route path="/storia" element={<ArticlePage />} />
          </Routes>
        </div>
      </div>

      {/* Footer */}
      <footer className="container mx-auto p-4 mt-4 md:mt-8 border-t border-terminal-green/30 text-center text-terminal-green/70 text-sm">
        <p>© 2025 Noctis AI</p>
      </footer>
    </div>
  );
};

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;